import React, { useEffect, useState } from 'react';
import { Button, Modal } from '../../../shared/components';
import { notificationsService } from '../../../shared/services/notificationsService';
import { getNotificationTypeLabel } from './notificationTypes';
import MessagePreview from './MessagePreview';

const formatDate = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour12: false });
};

/**
 * Corrige el teléfono de una familia cuyo número Meta no pudo entregar (131026):
 * actualiza los alumnos, reenvía los mensajes fallidos y cancela los originales.
 */
const PhoneCorrectionModal = ({ notification, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);

  const notificationId = notification?.id;

  useEffect(() => {
    if (!notificationId) return undefined;
    let active = true;
    setPreview(null);
    setViewing(null);
    setError(null);
    setLoading(true);
    notificationsService
      .previewPhoneCorrection(notificationId)
      .then((data) => {
        if (!active) return;
        setPreview(data);
        setPhone(data.currentPhone);
      })
      .catch((err) => active && setError(err.message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [notificationId]);

  const unchanged = !preview || phone.trim() === '' || phone.trim() === preview.currentPhone;
  const canConfirm = !!preview && !unchanged && !submitting;

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await notificationsService.applyPhoneCorrection(notificationId, phone.trim());
      await onDone(
        `Teléfono corregido a ${result.newPhone}: ${result.studentsUpdated} alumno(s) actualizados y ` +
          `${result.resent} mensaje(s) reenviados.`
      );
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (notification && viewing) {
    return (
      <Modal isOpen onClose={onClose} title="Mensaje que se reenviaría" size="lg">
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            {getNotificationTypeLabel(viewing.targetType)}
            {viewing.targetType !== viewing.type && (
              <span> (reemplaza a {getNotificationTypeLabel(viewing.type)})</span>
            )}{' '}
            · al teléfono nuevo
          </div>
          <MessagePreview message={viewing.message} />
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setViewing(null)}>
              Volver
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={!!notification} onClose={onClose} title="Corregir teléfono" size="lg">
      <div className="space-y-4">
        {loading && <p className="text-sm text-gray-500">Cargando…</p>}

        {preview && (
          <>
            <p className="text-sm text-gray-700">
              Meta informó que el número{' '}
              <span className="font-mono font-semibold">{preview.currentPhone}</span> no puede
              recibir mensajes.
            </p>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">
                Alumnos que se actualizan ({preview.students.length})
              </h3>
              {preview.students.length === 0 ? (
                <p className="text-sm text-gray-500">Ningún alumno tiene este número.</p>
              ) : (
                <ul className="text-sm text-gray-800 divide-y divide-gray-100 border border-gray-200 rounded-lg">
                  {preview.students.map((s) => (
                    <li key={s.id} className="px-3 py-2">
                      {s.name} <span className="text-gray-500">· {s.course || 'Sin curso'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-1">
                Mensajes que se reenvían ({preview.messages.length})
              </h3>
              <ul className="text-xs text-gray-700 divide-y divide-gray-100 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                {preview.messages.map((m) => (
                  <li key={m.id} className="px-3 py-2 flex items-center justify-between gap-2">
                    <span>
                      {getNotificationTypeLabel(m.type)} · {formatDate(m.createdAt)}
                      {m.providerErrorCode && (
                        <span className="font-mono text-red-700"> · código {m.providerErrorCode}</span>
                      )}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => setViewing(m)}>
                      Ver
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <label htmlFor="new-phone" className="block text-sm font-semibold text-gray-700 mb-1">
                Teléfono nuevo
              </label>
              <input
                id="new-phone"
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Número argentino: código de área y número, p. ej. 11 2222-3333 (se agrega +54 9).
                Número del exterior: con signo + y código de país, p. ej. +34 600 123 456.
              </p>
            </div>
          </>
        )}

        {error && <p className="text-sm text-red-700">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            {submitting ? 'Corrigiendo...' : 'Corregir y reenviar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PhoneCorrectionModal;
